import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type FormDefinition } from "@njbsoft/form-logic";
import { parseFormDefinition } from "@njbsoft/form-renderer";
import {
  useFormTemplate,
  useTemplateVersion,
  useTemplateVersions,
} from "@/hooks/use-form-template";
import { BUILDER_SCHEMA_VERSION, computeLockedLinkIds } from "@njbsoft/form-builder";
import { BuilderShell } from "@/components/form-templates/builder/builder-shell";
import { usePermissions } from "@/hooks/use-permissions";
import { Breadcrumbs, Card, PageHeader, PageSkeleton } from "@/components/ui";

const EMPTY_LOCKED: ReadonlySet<string> = new Set<string>();

export const Route = createFileRoute("/_authenticated/test-program/templates/$id/builder")({
  component: TemplateBuilderPage,
});

/** Route-guard banner for direct navigation that must not mount the editor. */
function GuardBanner({ templateId, message }: { templateId: string; message: string }) {
  const { t } = useTranslation(["form-templates"]);
  return (
    <Card>
      <p className="text-sm text-on-surface">{message}</p>
      <Link
        to="/test-program/templates/$id"
        params={{ id: templateId }}
        className="mt-3 inline-block text-sm font-medium text-primary hover:underline"
      >
        {t("builder.backToDetail")}
      </Link>
    </Card>
  );
}

function TemplateBuilderPage() {
  const { t } = useTranslation(["form-templates", "nav"]);
  const { id } = Route.useParams();

  const { can } = usePermissions();
  const canUpdate = can("form_templates", "update");

  const { data: template, isLoading, isError } = useFormTemplate(id);
  const versionsQuery = useTemplateVersions(id);

  // Fork-source load: inherited bound items are LOCKED in the builder. The
  // family-pinned version read works through the global+org overlay (system
  // rows included), keyed by the server-resolved source family id.
  const sourceVersionQuery = useTemplateVersion(
    template?.derived_from_template_id ?? null,
    template?.derived_from_version_id ?? null,
  );
  const isFork = Boolean(template?.derived_from_version_id && template?.derived_from_template_id);
  const sourceDefinition = sourceVersionQuery.data?.definition;
  const lockedLinkIds = useMemo<ReadonlySet<string>>(() => {
    if (!isFork || sourceDefinition === undefined) return EMPTY_LOCKED;
    // The stored source definition is an opaque JSON object validated by the
    // publish gates; published documents always carry the sections shape.
    const parsed = parseFormDefinition(sourceDefinition);
    if (!Array.isArray(parsed.sections)) return EMPTY_LOCKED;
    return computeLockedLinkIds(parsed);
  }, [isFork, sourceDefinition]);

  if (isLoading || versionsQuery.isLoading || (isFork && sourceVersionQuery.isLoading)) {
    return (
      <div className="p-6">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !template || versionsQuery.isError) {
    return (
      <div className="p-6">
        <Breadcrumbs
          items={[
            {
              label: t("testProgram.templates", { ns: "nav" }),
              to: "/test-program",
              search: { tab: "templates" },
            },
          ]}
        />
        <PageHeader title={t("errors.loadDetailFailed")} />
      </div>
    );
  }

  const breadcrumbs = (
    <Breadcrumbs
      items={[
        {
          label: t("testProgram.templates", { ns: "nav" }),
          to: "/test-program",
          search: { tab: "templates" },
        },
        { label: template.title, to: "/test-program/templates/$id", params: { id } },
        { label: t("builder.title") },
      ]}
    />
  );

  // Editing requires the form_templates:update grant, on live org-owned
  // families only: system rows are seed data (fork to edit a copy) and archived rows are
  // historical records. Direct navigation renders the guard banner instead
  // of the editor.
  const guardMessage = !canUpdate
    ? t("builder.guard.adminOnly")
    : template.org_id === null
      ? t("builder.guard.systemTemplate")
      : template.deleted_at
        ? t("builder.guard.archived")
        : null;

  if (guardMessage !== null) {
    return (
      <div className="space-y-6 p-6">
        {breadcrumbs}
        <PageHeader title={t("builder.title")} />
        <GuardBanner templateId={id} message={guardMessage} />
      </div>
    );
  }

  const versions = versionsQuery.data?.items ?? [];
  const draft = versions.find((v) => v.status === "draft");
  const latestPublished = versions
    .filter((v) => v.status === "published")
    .sort((a, b) => b.version - a.version)[0];

  // The reducer seeds from the server draft row; with no draft yet, the
  // latest published document is the "new draft" starting point (the first
  // autosave PUT creates the draft row — one-draft create-if-absent).
  const seedVersion = draft ?? latestPublished;
  const seedDefinition: FormDefinition = seedVersion
    ? // The wire definition is an opaque JSON object; drafts/publishes are
      // written from this document model, so the narrowing is safe once
      // the `sections` array shape is confirmed below.
      parseFormDefinition(seedVersion.definition)
    : {
        schema_version: BUILDER_SCHEMA_VERSION,
        target: template.target_kind === "device_test" ? "device" : "premise",
        sections: [],
      };

  // The bridge narrows, it does not validate: a draft saved without sections
  // arrives typed and unrenderable, so guard before mounting the editor.
  if (!Array.isArray(seedDefinition.sections)) {
    return (
      <div className="space-y-6 p-6">
        {breadcrumbs}
        <PageHeader title={t("builder.title")} />
        <GuardBanner templateId={id} message={t("builder.guard.unrenderable")} />
      </div>
    );
  }

  // The editor branch alone is full-bleed and viewport-bound: the shell owns
  // the breadcrumbs + sticky header, and each pane scrolls independently. The
  // guard / skeleton / error branches above keep the page wrapper.
  return (
    <div className="flex h-full min-h-0 flex-col">
      <BuilderShell
        // Remount (and reseed the reducer) when the backing draft row changes.
        key={seedVersion?.id ?? "new-draft"}
        templateId={id}
        templateTitle={template.title}
        seedDefinition={seedDefinition}
        seedReviewRequired={seedVersion?.review_required}
        lockedLinkIds={lockedLinkIds}
        sourceLockUnavailable={isFork && sourceVersionQuery.isError}
      />
    </div>
  );
}
